import { TEMPLATE_ORGANIZATION, GFA_QUOTA_UNLIMITED } from '@shared/constants';
import { Recipe } from '@shared/models/recipe.model';
import { IProjectFolder } from '../models/folder.model';
import { IOrganizationWhitelistItem } from '../models/organization.model';
import { IProduct } from '../models/product.model';
import { IProjectInfo, Project } from '../models/project.model';
import {
  ArrayOrSingle,
  OptionallyRequired,
} from '../models/type-helpers.model';
import { UserOrganization } from '../models/user.model';
import { includesSome } from './array.helpers';
import { required } from './function.helpers';
import { isProduct } from './product.helpers';
import { isArray } from 'lodash';

/**
 * Items that can have organizations
 */
type OrganizationItems =
  Recipe | IProduct | Project | IProjectInfo | IProjectFolder;

/**
 * Organizations or items that can have organizations
 */
type OrganizationsOrItems = OrganizationItems | ArrayOrSingle<string>;

export const getOrganizationQuota = ({
  display_name: _display_name,
  metadata,
}: UserOrganization): number | undefined => {
  if (!metadata || metadata.gfa_quota === GFA_QUOTA_UNLIMITED) {
    return;
  }

  return Number(metadata.gfa_quota);
};

/**
 * Whether the an organization allows showing product rows in Element List.
 * The metadata is stored as a string on the organization: show_element_list_product_rows?: string
 */
export const getShowElementListProductRows = (
  selectedOrg?: UserOrganization,
): boolean => {
  return selectedOrg?.metadata?.show_element_list_product_rows === 'true';
};

/**
 * Whether the organization has approved AI chat features.
 * Stored as `ai_chatbot_approved` string on {@link UserOrganization} metadata.
 */
export const getIsAiChatApproved = (org?: UserOrganization): boolean => {
  return org?.metadata?.ai_chatbot_approved === 'true';
};

/**
 * Check if an item is from the template organization
 * @param item The item to check
 * @returns
 */
export const isTemplateOrganizationItem = (
  item?: OrganizationsOrItems,
): boolean => {
  return getOrganizations(item).includes(TEMPLATE_ORGANIZATION);
};

/**
 * Get organizations from any kind of organization item
 * @param orgItem
 * @returns
 */
export const getOrganizations = (orgItem?: OrganizationsOrItems): string[] => {
  if (!orgItem) {
    return [];
  }
  if (typeof orgItem === 'string') {
    return [orgItem];
  }
  if (Array.isArray(orgItem)) {
    return orgItem;
  }
  if ('organizations' in orgItem && Array.isArray(orgItem.organizations)) {
    return orgItem.organizations;
  }
  return []; // Generic products might lack the empty array
};

/**
 * Everything currently only supports one organization, this function will return the first organization found
 * @param orgItem
 * @param required Whether to throw an error if the organization is not found
 * @returns
 */
export const getOrganization = <T extends boolean = true>(
  orgItem?: OrganizationsOrItems,
  requireOrg: T = true as T,
): OptionallyRequired<string, T> => {
  const errorMsg = requireOrg ? 'Organization not found' : false;
  const orgs = getOrganizations(orgItem);
  return required(orgs[0], errorMsg);
};

/**
 * Check if the item belongs to the user organization(s)
 * @param item The item to check
 * @param organizations The organizations the user has access to
 * @param includeTemplate Whether to include the template organization, default is true
 * @returns
 */
export const hasOrganizationReadAccessToItem = (
  item: OrganizationItems | undefined,
  organizations: ArrayOrSingle<string> | undefined,
  includeTemplate?: boolean,
): boolean => {
  // Generic products are available to all organizations by not specifying any organizations
  if (isProduct(item) && !item.organizations?.length) {
    return true;
  }

  return hasOrganizationAccess(
    item?.organizations,
    organizations,
    includeTemplate,
  );
};

/**
 * Check user have access to any of the organizations of the item
 * @param itemOrgs The organizations of the item
 * @param userOrgs The organizations the user has access to
 * @param includeTemplate Whether to include the template organization, default is true
 * @returns
 */
export const hasOrganizationAccess = (
  itemOrgs: OrganizationsOrItems | undefined,
  userOrgs: OrganizationsOrItems | undefined,
  includeTemplate = true,
): boolean => {
  return includesSome(
    getOrganizations(itemOrgs),
    ...getOrganizations(userOrgs),
    includeTemplate ? TEMPLATE_ORGANIZATION : undefined,
  );
};

/**
 * Check if an item is readonly by the selected organization. Like a product from the template organization used in a client organization.
 * Editing these items is allowed from the template organization (only accessed by nodon admins)
 * @param selectedOrganization The selected organization
 * @param orgItem The item to check
 * @returns
 */
export const isReadonlyBySelectedOrganization = (
  selectedOrganization: string | undefined,
  orgItem: OrganizationsOrItems | undefined,
): boolean => {
  const orgs = getOrganizations(orgItem);

  // Generic products don't have organizations and should be readonly by default
  if (!orgs.length) {
    return true;
  }
  return isTemplateOrganizationItem(selectedOrganization)
    ? false // We don't want to consider template items readonly when browsing the template organization
    : isTemplateOrganizationItem(orgs); // Check if the item belongs to the template organization
};

/**
 * Check if an item is whitelisted by an organization (Should be visible)
 * @param whitelistItem The item to check, could be an item or an array of organizations
 * @param organization The organization to check
 * @returns True if the organization is whitelisted (or no whitelist is set)
 */
export const isOrganizationWhitelisted = (
  whitelistItem:
    | IOrganizationWhitelistItem
    | IOrganizationWhitelistItem['organizationWhitelist']
    | undefined,
  organization: string | undefined,
): boolean => {
  const whitelist = isArray(whitelistItem)
    ? whitelistItem
    : whitelistItem?.organizationWhitelist;

  // No whitelist means the item is available to all organizations
  if (!whitelist) {
    return true;
  }
  return hasOrganizationAccess(whitelist, organization, false);
};
